import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import Input from '@/components/Forms/Input'
import Button from '@/components/UI/Button'
import { apiUrl } from '@/utils/api'

const LoginPage = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // Make actual API call to backend
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })
      
      const result = await response.json()
      
      if (result.success) {
        // Store auth token
        localStorage.setItem('admin_token', result.data.token)
        localStorage.setItem('admin_user', JSON.stringify({
          ...result.data.user,
          loginTime: new Date().toISOString()
        }))
        
        navigate('/admin/dashboard')
      } else {
        setError(result.error?.message || 'Invalid credentials.')
      }
    } catch (err) {
      setError('Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <>
      <Helmet>
        <title>Admin Login - CyberScrape Training Platform</title>
        <meta name="description" content="Instructor login for monitoring dashboard access" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-800 via-primary-700 to-accent-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-md w-full space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="mx-auto h-16 w-16 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              <ShieldCheckIcon className="h-8 w-8 text-accent-800" />
            </motion.div>
            
            <h2 className="mt-6 text-3xl font-bold text-white">
              Instructor Access
            </h2>
            <p className="mt-2 text-sm text-primary-100">
              CyberScrape Training Platform Monitoring Dashboard
            </p>
          </div>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="bg-white rounded-lg shadow-xl p-8"
          >
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-lg p-4"
                >
                  <p className="text-red-700 text-sm">{error}</p>
                </motion.div>
              )}

              <div>
                <Input
                  label="Username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Enter your username"
                  leftIcon={<ShieldCheckIcon />}
                />
              </div>

              <div>
                <Input
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Enter your password"
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  }
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading || !formData.username || !formData.password}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            {/* Educational Note */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-600">
                This is an educational platform for cybersecurity training.
                <br />
                All activities are monitored and logged for learning purposes.
              </p>
            </div>
          </motion.div>

          {/* Footer Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.6 }}
            className="text-center text-sm text-primary-200"
          >
            <p>
              Need help? Contact your system administrator or visit the{' '}
              <a href="/" className="text-gold-200 hover:text-white transition-colors duration-200">
                main platform
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </>
  )
}

export default LoginPage
